import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import apiClient from "utils/apiClient";
import paths from "routes/paths";

const TASKS_BASE = "/olitask-service/api/v1/tasks";
const USERS_BASE = "/profile-service/api/v1/user-profiles";
const PROJECTS_BASE = "/pm-service/api/v1/projects";

const EMPTY_YEAR = new Date().getFullYear();
const EMPTY_TOTAL_SALES = {
  [EMPTY_YEAR]: Array.from({ length: 12 }, () =>
    Array.from({ length: 12 }, () => 0),
  ),
};

const STATUS_COLORS = {
  COMPLETED: "success",
  CANCELLED: "danger",
  EXPIRED: "secondary",
};

const MARKET_SHARE_STATUSES = [
  { keys: ["COMPLETED"], name: "Completed", color: STATUS_COLORS.COMPLETED },
  { keys: ["CANCELLED"], name: "Cancelled", color: STATUS_COLORS.CANCELLED },
  { keys: ["EXPIRED"], name: "Expired", color: STATUS_COLORS.EXPIRED },
];

const RUNNING_PROJECT_EXCLUDED = new Set([
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
  "ARCHIVED",
  "INACTIVE",
]);

const PROJECT_FALLBACK_COLORS = ["primary", "success", "info", "warning", "danger"];

const PAGE_SIZE = 500;
const STATUS_COUNTS_TTL_MS = 10 * 60 * 1000;
const ALL_STATUSES = [
  "NEW",
  "PENDING",
  "ACCEPTED",
  "NAVIGATING",
  "IN_PROGRESS",
  "ARRIVED",
  "IN_REVIEW",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
  "DEACTIVATED",
  "DELETED",
  "FLAGGED",
  "BLOCKED",
];

const getPage = (payload) => {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalElements: payload.length,
    };
  }

  if (Array.isArray(payload?.content)) {
    return {
      content: payload.content,
      totalElements:
        typeof payload.totalElements === "number"
          ? payload.totalElements
          : payload.content.length,
    };
  }

  if (Array.isArray(payload?.data)) {
    return {
      content: payload.data,
      totalElements: payload.data.length,
    };
  }

  return {
    content: [],
    totalElements: 0,
  };
};

const compactCount = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

const calculateTrend = (current, previous) => {
  if (!previous && !current) return { label: "0%", up: true };
  if (!previous) return { label: "+100%", up: true };

  const delta = ((current - previous) / previous) * 100;
  const normalized = Number.isFinite(delta) ? delta : 0;
  const sign = normalized >= 0 ? "+" : "";
  return {
    label: `${sign}${normalized.toFixed(1)}%`,
    up: normalized >= 0,
  };
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const buildWeeklySeries = (tasks) => {
  const now = new Date();
  const last7Start = new Date(now);
  last7Start.setDate(now.getDate() - 6);
  last7Start.setHours(0, 0, 0, 0);

  const prev7Start = new Date(last7Start);
  prev7Start.setDate(last7Start.getDate() - 7);

  const series = Array.from({ length: 7 }, () => 0);
  let currentWeekCount = 0;
  let previousWeekCount = 0;

  tasks.forEach((task) => {
    const createdAt = parseDate(task?.createdAt);
    if (!createdAt) return;

    if (createdAt >= last7Start) {
      series[createdAt.getDay()] += 1;
      currentWeekCount += 1;
      return;
    }

    if (createdAt >= prev7Start && createdAt < last7Start) {
      previousWeekCount += 1;
    }
  });

  return {
    series,
    ...calculateTrend(currentWeekCount, previousWeekCount),
  };
};

const buildUserSeries = (users) => {
  const now = new Date();
  const series = [0, 0, 0, 0];
  let current28Count = 0;
  let previous28Count = 0;

  users.forEach((user) => {
    const createdAt = parseDate(user?.createdAt || user?.createdOn);
    if (!createdAt) return;

    const diffDays = Math.floor((now - createdAt) / (24 * 60 * 60 * 1000));
    if (diffDays < 0) return;

    if (diffDays < 28) {
      const weekBucket = Math.min(3, Math.floor(diffDays / 7));
      series[3 - weekBucket] += 1;
      current28Count += 1;
      return;
    }

    if (diffDays < 56) {
      previous28Count += 1;
    }
  });

  return {
    series,
    ...calculateTrend(current28Count, previous28Count),
  };
};

const buildSubmittedSeriesByYear = (tasks) => {
  const yearMap = {};

  tasks.forEach((task) => {
    const submittedAt = parseDate(task?.updatedAt || task?.createdAt);
    if (!submittedAt) return;

    const year = submittedAt.getFullYear();
    const monthIndex = submittedAt.getMonth();
    const day = submittedAt.getDate();
    const dayBucket = Math.min(11, Math.floor((day - 1) / 3));

    if (!yearMap[year]) {
      yearMap[year] = Array.from({ length: 12 }, () =>
        Array.from({ length: 12 }, () => 0),
      );
    }

    yearMap[year][monthIndex][dayBucket] += 1;
  });

  const years = Object.keys(yearMap)
    .map((y) => Number(y))
    .sort((a, b) => b - a);

  if (!years.length) {
    return {
      years: [EMPTY_YEAR],
      dataByYear: EMPTY_TOTAL_SALES,
    };
  }

  return {
    years,
    dataByYear: yearMap,
  };
};

const getProjectProgress = (project) => {
  const status = String(project?.status || "").toUpperCase();
  if (status === "COMPLETED") return 100;
  if (status === "CANCELLED" || status === "EXPIRED" || status === "ARCHIVED") return 0;

  const start = parseDate(project?.startDate);
  const end = parseDate(project?.endDate);
  if (!start || !end || end <= start) return 60;

  const now = new Date();
  const total = end.getTime() - start.getTime();
  const elapsed = Math.max(0, Math.min(now.getTime() - start.getTime(), total));
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
};

const getProjectDuration = (project) => {
  const end = parseDate(project?.endDate);
  if (!end) {
    return `${project?.taskCount || 0} tasks`;
  }

  const now = new Date();
  const diffDays = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
  if (diffDays >= 0) return `${diffDays}d left`;
  return `${Math.abs(diffDays)}d overdue`;
};

const mapRunningProjects = (projects = []) => {
  const normalized = projects.map((project, index) => ({
    id: project?.id || `project-${index}`,
    title: project?.name || project?.code || "Untitled project",
    progress: getProjectProgress(project),
    duration: getProjectDuration(project),
    color: PROJECT_FALLBACK_COLORS[index % PROJECT_FALLBACK_COLORS.length],
    status: String(project?.status || "").toUpperCase(),
  }));

  const running = normalized.filter(
    (project) => !RUNNING_PROJECT_EXCLUDED.has(project.status),
  );

  return (running.length ? running : normalized).slice(0, 5);
};

const toPct = (num, den) => {
  if (!den) return "0%";
  return `${((num * 100) / den).toFixed(1)}%`;
};

const createEmptyStatusCounts = () =>
  ALL_STATUSES.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});

export default function useHomeDashboardData() {
  const latestFetchIdRef = useRef(0);
  const inFlightDashboardFetchRef = useRef(null);
  const statusCountsCacheRef = useRef(null);
  const statusCountsFetchedAtRef = useRef(0);
  const statusCountsInFlightRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState({
    weeklySalesData: Array.from({ length: 7 }, () => 0),
    weeklySalesTotal: 0,
    weeklySalesTrendLabel: "0%",
    weeklySalesTrendUp: true,
    totalOrderData: [0, 0, 0, 0],
    totalOrderTotal: 0,
    totalOrderTrendLabel: "0%",
    totalOrderTrendUp: true,
    totalSalesByYear: EMPTY_TOTAL_SALES,
    submittedYears: [EMPTY_YEAR],
    marketShareData: [
      ...MARKET_SHARE_STATUSES.map((status, index) => ({
        id: index + 1,
        value: 0,
        name: status.name,
        color: status.color,
      })),
    ],
    runningProjectsData: [],
    statusCounts: {},
    healthMetrics: {
      completionRate: "0%",
      interruptionRate: "0%",
      inFlightRate: "0%",
      completedCount: 0,
      interruptedCount: 0,
      inFlightCount: 0,
      totalTracked: 0,
    },
  });

  const fetchStatusCounts = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    const hasFreshCache =
      statusCountsCacheRef.current &&
      now - statusCountsFetchedAtRef.current < STATUS_COUNTS_TTL_MS;

    if (!force && hasFreshCache) {
      return statusCountsCacheRef.current;
    }

    if (statusCountsInFlightRef.current) {
      return statusCountsInFlightRef.current;
    }

    const request = (async () => {
      try {
        const statusRequests = ALL_STATUSES.map((status) =>
          apiClient.get(`${TASKS_BASE}/filter?status=${status}&page=0&size=1`),
        );
        const statusResults = await Promise.allSettled(statusRequests);
        const nextStatusCounts = createEmptyStatusCounts();

        ALL_STATUSES.forEach((status, index) => {
          const result = statusResults[index];
          if (result?.status === "fulfilled") {
            nextStatusCounts[status] = getPage(result.value?.data).totalElements;
          }
        });

        statusCountsCacheRef.current = nextStatusCounts;
        statusCountsFetchedAtRef.current = Date.now();
        return nextStatusCounts;
      } catch (error) {
        console.error("Failed to fetch dashboard status counts:", error);
        return statusCountsCacheRef.current || createEmptyStatusCounts();
      }
    })();

    statusCountsInFlightRef.current = request;

    try {
      return await request;
    } finally {
      if (statusCountsInFlightRef.current === request) {
        statusCountsInFlightRef.current = null;
      }
    }
  }, []);

  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    const token = sessionStorage.getItem("authToken");
    if (!token) return;

    if (inFlightDashboardFetchRef.current) {
      return inFlightDashboardFetchRef.current;
    }

    const runFetch = (async () => {
      const fetchId = ++latestFetchIdRef.current;
      setSyncing(true);
      if (!silent) setLoading(true);

      try {
        const [baseResults, statusCounts] = await Promise.all([
          Promise.allSettled([
            apiClient.get(`${TASKS_BASE}?page=0&size=${PAGE_SIZE}`),
            apiClient.get(`${USERS_BASE}?page=0&size=${PAGE_SIZE}&refresh=true`),
            apiClient.get(`${PROJECTS_BASE}?page=0&size=20`),
          ]),
          fetchStatusCounts({ force: !silent }),
        ]);

        const [tasksResult, usersDetailsResult, projectsResult] = baseResults;
        const tasksPage = getPage(tasksResult.value?.data);
        const usersDetailsPage = getPage(usersDetailsResult.value?.data);
        const projectsPage = getPage(projectsResult.value?.data);

        const weekly = buildWeeklySeries(tasksPage.content);
        const userStats = buildUserSeries(usersDetailsPage.content);
        const submittedTasks = tasksPage.content.filter((task) => {
          const status = String(task?.status || "").toUpperCase();
          return status === "IN_REVIEW" || status === "COMPLETED";
        });
        const submittedSeries = buildSubmittedSeriesByYear(submittedTasks);
        const completed = statusCounts.COMPLETED || 0;
        const interrupted =
          (statusCounts.CANCELLED || 0) +
          (statusCounts.EXPIRED || 0) +
          (statusCounts.DEACTIVATED || 0) +
          (statusCounts.DELETED || 0);
        const trackedTotal = Object.values(statusCounts).reduce(
          (acc, value) => acc + (Number(value) || 0),
          0,
        );
        const inFlight = Math.max(0, trackedTotal - completed - interrupted);

        if (fetchId !== latestFetchIdRef.current) return;

        setData({
          weeklySalesData: weekly.series,
          weeklySalesTotal: tasksPage.totalElements,
          weeklySalesTrendLabel: weekly.label,
          weeklySalesTrendUp: weekly.up,

          totalOrderData: userStats.series,
          totalOrderTotal: usersDetailsPage.totalElements,
          totalOrderTrendLabel: userStats.label,
          totalOrderTrendUp: userStats.up,

          totalSalesByYear: submittedSeries.dataByYear,
          submittedYears: submittedSeries.years,

          marketShareData: MARKET_SHARE_STATUSES.map((status, index) => ({
            id: index + 1,
            value: status.keys.reduce(
              (acc, key) => acc + (statusCounts[key] || 0),
              0,
            ),
            name: status.name,
            color: status.color,
          })),

          runningProjectsData: mapRunningProjects(projectsPage.content),
          statusCounts,
          healthMetrics: {
            completionRate: toPct(completed, trackedTotal),
            interruptionRate: toPct(interrupted, trackedTotal),
            inFlightRate: toPct(inFlight, trackedTotal),
            completedCount: completed,
            interruptedCount: interrupted,
            inFlightCount: inFlight,
            totalTracked: trackedTotal,
          },
        });
      } catch (error) {
        console.error("Failed to fetch home dashboard data:", error);
      } finally {
        if (!silent && fetchId === latestFetchIdRef.current) {
          setLoading(false);
        }
        if (fetchId === latestFetchIdRef.current) {
          setSyncing(false);
        }
      }
    })();

    inFlightDashboardFetchRef.current = runFetch;

    try {
      return await runFetch;
    } finally {
      if (inFlightDashboardFetchRef.current === runFetch) {
        inFlightDashboardFetchRef.current = null;
      }
    }
  }, [fetchStatusCounts]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const refresh = () => fetchDashboardData({ silent: true });

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);

    const interval = setInterval(refresh, 60 * 1000);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [fetchDashboardData]);

  return useMemo(
    () => ({
      loading,
      syncing,
      refetch: fetchDashboardData,
      weeklySalesData: data.weeklySalesData,
      weeklySalesTotalLabel: compactCount(data.weeklySalesTotal),
      weeklySalesTrendLabel: data.weeklySalesTrendLabel,
      weeklySalesTrendUp: data.weeklySalesTrendUp,
      weeklySalesLink: paths.tasks,

      totalOrderData: data.totalOrderData,
      totalOrderTotalLabel: compactCount(data.totalOrderTotal),
      totalOrderTrendLabel: data.totalOrderTrendLabel,
      totalOrderTrendUp: data.totalOrderTrendUp,
      totalOrderLink: paths.users,

      totalSalesByYear: data.totalSalesByYear,
      submittedYears: data.submittedYears,
      totalSalesLink: paths.tasks,
      marketShareData: data.marketShareData,
      marketShareLink: paths.tasks,
      runningProjectsData: data.runningProjectsData,
      runningProjectsLink: paths.projects,
      statusCounts: data.statusCounts,
      healthMetrics: data.healthMetrics,
    }),
    [data, fetchDashboardData, loading, syncing],
  );
}
